import { isFeatureOn } from 'featureToggles';
import { byCountry } from 'utils/byConfig';
import { isGemAUWithMerchantApple } from 'utils';
import { OKTA_SMS_FACTOR } from 'store';

import {
  STEP_CONFIRM_INCOME,
  STEP_BIOMETRICS_COMPLETE,
  STEP_BIOMETRICS_CANCELLED,
  STEP_BIOMETRICS_RETURN,
  STEP_BIOMETRICS_IDENTIFICATION,
  STEP_BIOMETRICS_ADDRESS_DETAILS,
  STEP_LATITUDE_ID_INTRODUCTION,
  STEP_LATITUDE_ID,
  STEP_EIDV,
  STEP_PROCESSING_IDV,
  STEP_DAVID_JONES_PRESTIGE,
  STEP_DAVID_JONES_PREMIERE,
  STEP_DVS_CONSENT,
  STEP_VERIFY_DOCUMENT_UPLOAD,
  STEP_LOAN_INFORMATION,
  STEP_PROCESSING_LOAN_INFORMATION,
  STEP_PRODUCT_COMPARISON,
  STEP_SAVED,
  STEP_GO,
  STEP_TWENTY_EIGHT_DEGREES,
  STEP_AUS_POST,
  STEP_PROCESSING_BIOMETRICS,
  STEP_ADDITIONAL_CARD_HOLDER,
  STEP_EMAIL_VERIFICATION,
  STEP_CREDIT_LINE,
  STEP_GEM_AU,
  STEP_VERIFY_INSTORE,
  STEP_LOW_RATE,
  STEP_VERIFY_APPLICATION_MOBILE,
  STEP_CONFIRM_CREDIT_LIMIT,
  STEP_AGREE_AND_CONTINUE,
  STEP_CONTACT_DETAILS,
  STEP_VERIFY,
  STEP_ABOUT_YOU,
  STEP_ADDRESS_DETAILS,
  STEP_INCOME,
  STEP_EXPENSES,
  STEP_COMMITMENTS,
  STEP_NZ_GEM_V2,
  STEP_PROOF_OF_ADDRESS,
  STEP_VERIFY_IDENTITY,
  STEP_PASSWORD,
  STEP_ID_DOC_UPLOAD,
  STEP_FACIAL_BIOMETRICS_CONSENT,
  STEP_BALANCE_TRANSFER,
  STEP_CREDIT_LIMIT_REQUEST,
  STEP_CREDIT_LIMIT_ALLOCATION,
} from '../index';

const overviewConfig = { header: '', progress: 'Overview', footer: 'OVERVIEW' };
const progressSteps = [
  STEP_CONTACT_DETAILS,
  STEP_VERIFY,
  STEP_ABOUT_YOU,
  STEP_ADDRESS_DETAILS,
  STEP_INCOME,
  STEP_EXPENSES,
  [STEP_COMMITMENTS, STEP_PASSWORD],
  [
    STEP_ADDITIONAL_CARD_HOLDER,
    STEP_BALANCE_TRANSFER,
    STEP_CONFIRM_CREDIT_LIMIT,
    STEP_CREDIT_LIMIT_REQUEST,
    STEP_LOAN_INFORMATION,
    STEP_CREDIT_LIMIT_ALLOCATION,
  ],
];

export function configByRouteMap(pathname, storeState, percentage) {
  return {
    '/': overviewConfig,
    [STEP_NZ_GEM_V2]: overviewConfig,
    [STEP_PRODUCT_COMPARISON]: { header: '', progress: 'Comparison', footer: 'OVERVIEW' },
    [STEP_DAVID_JONES_PRESTIGE]: overviewConfig,
    [STEP_DAVID_JONES_PREMIERE]: overviewConfig,
    [STEP_TWENTY_EIGHT_DEGREES]: overviewConfig,
    [STEP_GO]: overviewConfig,
    [STEP_GEM_AU]: overviewConfig,
    [STEP_CREDIT_LINE]: overviewConfig,
    [STEP_LOW_RATE]: overviewConfig,
    [STEP_AGREE_AND_CONTINUE]: { progress: 'You', percentage: 0 },
    '/contact-details': {
      header: 'Let’s get to know you',
      progress: 'You',
      percentage: 10,
    },
    '/resume-your-application': {
      header: 'Resume application',
      progress: 'You',
    },
    '/resume-acceptance': { header: 'Resume your acceptance', progress: 'You' },
    '/verify': {
      header: `Verify your ${
        storeState.oktaProfileHealthy && storeState.oktaFactor === OKTA_SMS_FACTOR ? 'mobile' : 'email'
      }`,
      progress: 'You',
      percentage: 20,
    },
    '/verify-to-resume': {
      header: `Verify your ${
        storeState.oktaProfileHealthy && storeState.oktaFactor === OKTA_SMS_FACTOR ? 'mobile' : 'email'
      }`,
      progress: 'You',
    },
    [STEP_VERIFY_APPLICATION_MOBILE]: { header: 'Verify your mobile', progress: 'You' },
    [STEP_LATITUDE_ID_INTRODUCTION]: { header: '', progress: 'Debts' },
    [STEP_LATITUDE_ID]: {
      header: byCountry({
        NZ: 'Create your password',
        AU: isFeatureOn('emailVerification') ? 'Create your password' : 'Create a Latitude ID',
      }),
      progress: byCountry({
        NZ: 'Debts',
        AU: isFeatureOn('emailVerification') ? 'Debts' : 'You',
      }),
    },
    [STEP_EMAIL_VERIFICATION]: {
      header: 'Verify your email',
      progress: 'Debts',
    },
    '/password': {
      header: 'Create your verbal password',
      progress: 'Debts',
      percentage: 75,
    },
    '/verify-identity': { header: 'Verify your identity', progress: 'You' },
    [STEP_PROCESSING_BIOMETRICS]: byCountry({
      NZ: {
        header: 'Verify your identity using facial verification',
        progress: 'Debts',
      },
      AU: { header: 'Biometric verification', progress: 'Debts' },
    }),
    [STEP_BIOMETRICS_COMPLETE]: { header: '', progress: 'You' },
    [STEP_BIOMETRICS_CANCELLED]: { header: '', progress: 'You' },
    [STEP_BIOMETRICS_RETURN]: { header: 'Welcome back!', progress: 'You' },
    '/biometrics-about-you': { header: 'About you', progress: 'You' },
    [STEP_BIOMETRICS_IDENTIFICATION]: { header: 'Identification', progress: 'Extra' },
    [STEP_BIOMETRICS_ADDRESS_DETAILS]: { header: 'Your address details', progress: 'Address' },
    [STEP_SAVED]: {
      header: 'Thank you for your application!',
      progress: 'You',
    },
    '/about-you': {
      header: 'Tell us about yourself',
      progress: 'You',
      percentage: 30,
    },
    '/address-details': {
      header: 'Your address details',
      progress: byCountry({
        AU: 'You',
        NZ: 'Address',
      }),
      percentage: 40,
    },
    '/identification': { header: 'Identification', progress: 'Extra' },
    '/income': {
      header: byCountry({
        NZ: 'Your employment details',
        AU: 'Your income details',
      }),
      progress: 'Work',
      percentage: 60,
    },
    '/expenses': {
      header: byCountry({
        AU: 'Your living expenses',
        NZ: 'Your living expenses',
      }),
      progress: byCountry({
        AU: 'Work',
        NZ: 'Expenses',
      }),
      percentage: 60,
    },
    '/commitments': {
      header: 'Other financial commitments',
      progress: byCountry({
        AU: 'Work',
        NZ: 'Debts',
      }),
      percentage: 70,
    },
    [STEP_ADDITIONAL_CARD_HOLDER]: {
      header: 'Add additional cardholder',
      progress: 'Work',
      percentage: 80,
    },
    '/credit-limit': { header: 'Choose your credit limit', progress: 'Debts', percentage: 90 },
    [STEP_BALANCE_TRANSFER]: { header: 'Balance Transfer', progress: 'Debts', percentage: 90 },
    [STEP_CREDIT_LIMIT_REQUEST]: { header: 'Credit limit request ', progress: 'Debts', percentage: 90 },
    '/verify-income': {
      header: byCountry({
        NZ: 'Verify Your Income',
        AU: 'Check your income',
      }),
      progress: 'Debts',
    },
    [STEP_LOAN_INFORMATION]: {
      header: 'Loan information',
      progress: 'Debts',
      percentage: 85,
    },
    [STEP_PROCESSING_LOAN_INFORMATION]: { header: '', progress: 'Debts' },
    [STEP_CONFIRM_CREDIT_LIMIT]: {
      header: byCountry({
        NZ: 'Tell us what credit limit meets your current circumstances',
        AU: null,
      }),
      progress: byCountry({
        AU: 'Work',
        NZ: 'Debts',
      }),
      percentage: 90,
    },
    [STEP_CREDIT_LIMIT_ALLOCATION]: {
      header: null,
      progress: 'Debts',
      percentage: 90,
    },
    [STEP_DVS_CONSENT]: { header: 'Check your identity', progress: 'Debts' },
    [STEP_EIDV]: { header: 'Check your identity', progress: 'Debts' },
    [STEP_FACIAL_BIOMETRICS_CONSENT]: { header: 'Verify your identity using facial verification', progress: 'Debts' },
    [STEP_CONFIRM_INCOME]: { header: 'Confirm your income', progress: 'Debts' },
    [STEP_VERIFY_DOCUMENT_UPLOAD]: { header: 'Check your income', progress: 'Debts' },
    '/verify-ficonnect': { progress: 'Debts' },
    [STEP_PROCESSING_IDV]: { header: '', progress: 'Debts' },
    [STEP_AUS_POST]: { header: 'Verify your identity at Australia Post', progress: 'Debts' },
    [STEP_VERIFY_IDENTITY]: { header: 'Verify your identity', progress: 'Work' },
    [STEP_PROOF_OF_ADDRESS]: { header: '', progress: 'Debts' },
    [STEP_VERIFY_INSTORE]: {
      header: isGemAUWithMerchantApple(storeState) ? null : 'Verify your identity in-store',
      progress: 'Debts',
    },
    [STEP_ID_DOC_UPLOAD]: { header: 'Alternative verification method', progress: 'Debts' },
    '/processing-poi': { header: '', progress: 'Debts' },
    '/processing-pending-poi': { header: '', progress: 'Debts' },
    // Note: STEP_PROCESSING_BIOMETRICS intentionally not duplicated here —
    // the canonical entry is the byCountry block above (line ~124). A
    // duplicate `{ header: '' }` here previously silenced both AU and
    // NZ V2 headers (OBS-16132 obs 2).
    '/processing': {
      header: '',
      progress: byCountry({
        AU: 'Work',
        NZ: 'Debts',
      }),
    },
    '/acceptance': {
      header: isFeatureOn('dynamicCreditLimit') ? 'Your preferred credit limit is' : 'Finalise your application',
      progress: 'Debts',
    },
    '/processing-acceptance': {
      header: '',
      progress: byCountry({
        NZ: 'Processing',
        AU: 'Debts',
      }),
    },
    '/outcome': { header: '', progress: 'Outcome', footer: 'OUTCOME' },
    '/outcome-timeout': { header: '', progress: 'Outcome', footer: 'OUTCOME' },
    '/outcome-fin-table': { header: '', progress: 'Outcome', footer: 'OUTCOME' },
    '/resume-application-expired': { header: '', progress: 'Outcome', footer: 'OUTCOME' },
  }[pathname];
}

export function getStepProgress(pathname) {
  const index = progressSteps.findIndex(step => (Array.isArray(step) ? step.includes(pathname) : step === pathname));
  return index === -1 ? 0 : (index + 1) * 10;
}
